import React, { useState } from 'react';
import { UserProfile, UserPrivacySettings } from '../../types';
import { Shield, Save, Eye, EyeOff, Lock, CheckCircle2 } from 'lucide-react';
import { soundEffects } from '../../lib/soundEffects';

interface Props {
  currentUser: UserProfile;
  onUpdateProfile: (profile: Partial<UserProfile>) => void;
}

export const PrivacySection: React.FC<Props> = ({ currentUser, onUpdateProfile }) => {
  const [privacy, setPrivacy] = useState<UserPrivacySettings>(
    currentUser.privacy || {
      lastSeen: 'everyone',
      readReceipts: true,
      whoCanCall: 'everyone',
      whoCanMessage: 'everyone',
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  const handleSave = async () => {
    soundEffects.playTap();
    setIsSaving(true);
    await onUpdateProfile({ privacy });
    setIsSaving(false);
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  const updatePrivacy = (key: keyof typeof privacy, val: any) => {
    setPrivacy({ ...privacy, [key]: val });
  };

  const visibilityOptions = [
    { value: 'everyone', label: 'الجميع' },
    { value: 'contacts', label: 'جهات الاتصال فقط' },
    { value: 'nobody', label: 'لا أحد' }
  ];

  const renderSelect = (label: string, key: keyof typeof privacy, options: typeof visibilityOptions) => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-[#141c2a] rounded-xl border border-slate-800">
      <div className="text-sm font-semibold text-slate-200">{label}</div>
      <select
        value={privacy[key] as string}
        onChange={(e) => updatePrivacy(key, e.target.value)}
        className="bg-[#0e1422] border border-slate-700 text-emerald-400 text-xs rounded-lg px-2 py-1.5 outline-none focus:border-emerald-500"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="bg-[#0e1422] rounded-3xl p-5 border border-slate-800/80 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-400" />
          الخصوصية
        </h3>

        <div className="space-y-3">
          {renderSelect('آخر ظهور ومتصل الآن', 'lastSeen', visibilityOptions)}
          {renderSelect('من يمكنه الاتصال بي', 'whoCanCall', visibilityOptions)}
          {renderSelect('من يمكنه رؤية رقم هاتفي', 'phoneVisibility' as any, visibilityOptions)}
          {renderSelect('من يمكنه رؤية صورتي الشخصية', 'avatarVisibility' as any, visibilityOptions)}

          <div className="flex items-center justify-between p-3 bg-[#141c2a] rounded-xl border border-slate-800">
            <div>
              <div className="text-sm font-semibold text-slate-200">مؤشرات قراءة الرسائل</div>
              <div className="text-[10px] text-slate-400 mt-1 max-w-[200px]">إذا قمت بتعطيل هذا الخيار لن تتمكن من رؤية مؤشرات قراءة الآخرين.</div>
            </div>
            <button
              onClick={() => updatePrivacy('readReceipts', !privacy.readReceipts)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacy.readReceipts ? 'bg-emerald-500' : 'bg-slate-700'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacy.readReceipts ? 'translate-x-1' : '-translate-x-6'}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-[#0e1422] rounded-3xl p-5 border border-slate-800/80 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-rose-400" />
          الأمان
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-[#141c2a] rounded-xl border border-slate-800">
            <div>
              <div className="text-sm font-semibold text-slate-200">المصادقة بخطوتين (2FA)</div>
              <div className="text-[10px] text-slate-400 mt-1 max-w-[200px]">طلب رمز إضافي عند تسجيل الدخول من جهاز جديد لزيادة الأمان.</div>
            </div>
            <button
              onClick={() => updatePrivacy('twoFactorEnabled' as any, !(privacy as any).twoFactorEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${(privacy as any).twoFactorEnabled ? 'bg-rose-500' : 'bg-slate-700'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${(privacy as any).twoFactorEnabled ? 'translate-x-1' : '-translate-x-6'}`} />
            </button>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-[#141c2a] rounded-xl border border-slate-800 cursor-pointer hover:border-emerald-500/30 transition">
            <div className="text-sm font-semibold text-rose-400">المستخدمون المحظورون</div>
            <div className="text-xs text-slate-400 font-bold bg-slate-800 px-2 py-0.5 rounded-lg">
              {currentUser.blockedUsers?.length || 0}
            </div>
          </div>
        </div>

        <button disabled={isSaving} onClick={handleSave} className={`mt-4 w-full py-2.5 font-bold rounded-xl flex items-center justify-center gap-2 transition active:scale-95 ${savedSuccess ? 'bg-emerald-400 text-slate-900' : 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 disabled:opacity-50'}`}>
          {savedSuccess ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {isSaving ? 'جاري الحفظ...' : savedSuccess ? 'تم الحفظ' : 'حفظ إعدادات الخصوصية'}
        </button>
      </div>
    </div>
  );
};
